import { useState } from 'react';
import { Avatar, Box, Skeleton, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { ApiService } from 'api';
import EditNoteDialog from 'dashboard409a/components/EditNoteDialog';
import { StyledSpanEmptyStatus, StyledStatusChip, StyledTableCell, StyledTableRow } from 'dashboard409a/components/ValuationsTable/common';
import { modelStatusMap } from 'dashboard409a/pages/Operations/components/ModelStatus/ModelStatus.constants';
import { getInitials } from 'utilities';
import { getModelStatusString, ModelWithValuationData } from '../../../services/models.types';
import { ModelsStatusDropdown } from '../ModelsStatusDropdown/ModelsStatusDropdown';
import { ModelsTableRowProps } from '../ModelsTable.types';


// Helper function to truncate text
const truncateText = (text: string | null | undefined, maxLength: number = 50): string => {
  if (!text || text === '-') {
    return text || '-';
  }
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.substring(0, maxLength).trim()}...`;
};

// Helper function to render notes cell
const renderNotesCell = (modelRow: ModelsTableRowProps['modelRow']) => {
  // Check if we have the new note structure
  if (modelRow.note) {
    return <Typography variant="body2">{truncateText(modelRow.note.content)}</Typography>;
  }
  // Fallback to old notes field for backward compatibility
  return <Typography variant="body2">{truncateText(modelRow.notes)}</Typography>;
};

// Helper function to render valuation lead cell
const renderValuationLeadCell = (modelRow: ModelsTableRowProps['modelRow']) => {
  if (modelRow.is_loading_valuation_data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Box>
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
    );
  }

  const valuationLead = modelRow.valuation_lead;
  const valuationLeadImageSrc = modelRow.valuation_lead_image_src;
  if (valuationLead && valuationLead !== '-') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          alt={valuationLead}
          src={valuationLeadImageSrc}
          sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
          {getInitials({ name: valuationLead })}
        </Avatar>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
            {valuationLead}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      -
    </Typography>
  );
};

const ModelsTableRow = ({
  modelRow,
  onRowClick,
  onNoteSave,
  onStatusUpdate,
  statusOptionsLoading,
  isNoteLoading = false,
  noteError = null,
}: ModelsTableRowProps) => {
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);

  const getFullName = (user: unknown) => {
    const userObj = user as { first_name?: string; last_name?: string };
    return `${userObj.first_name || ''} ${userObj.last_name || ''}`.trim();
  };
  
  // Check if status is final and should be read-only
  const isFinalStatus = modelRow.model_status === 3; // Final
  
  const handleStatusChange = async (newStatus: string) => {
    try {
      setIsUpdatingStatus(true);

      const apiStatusValue = modelStatusMap[newStatus as keyof typeof modelStatusMap];
      if (apiStatusValue === undefined) {
        enqueueSnackbar('Invalid status selected', { variant: 'error' });
        return;
      }

      const updatedModelRow = { model_status: apiStatusValue, company_measurement_date: modelRow.company_measurement_date };

      await ApiService.apiDashboardCompanyOperationsFulfillmentUpdateStatus(modelRow?.company_id.toString(), modelRow?.attio_valuation_id, updatedModelRow);

      if (onStatusUpdate && modelRow.id) {
        onStatusUpdate(modelRow.id, apiStatusValue);
      }

      setIsEditingStatus(false);
    } catch {
      enqueueSnackbar('Failed to update model status', { variant: 'error' });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleNoteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsNoteDialogOpen(true);
  };

  const handleNoteSave = async (newNote: string) => {
    if (onNoteSave) {
      await onNoteSave(newNote, modelRow.notes || '');
    }
  };

  return (
    <StyledTableRow
      hover
      tabIndex={-1}
      key={modelRow.id}
      style={{ background: '#ffff' }}
      onClick={event => onRowClick(modelRow, event)}>
      <StyledTableCell align="left">{modelRow.company_name || 'Unknown Company'}</StyledTableCell>
      <StyledTableCell align="left">
        <ModelsStatusDropdown
          currentStatus={getModelStatusString(modelRow.model_status || 0)}
          onStatusChange={handleStatusChange}
          isEditing={isEditingStatus}
          onEditEnd={() => setIsEditingStatus(false)}
          loading={statusOptionsLoading}
        />

        {!isEditingStatus && (
          <>
            {modelRow.model_status !== undefined ? (
              <StyledStatusChip
                label={isUpdatingStatus ? 'Updating...' : getModelStatusString(modelRow.model_status)}
                status={getModelStatusString(modelRow.model_status)}
                onClick={e => {
                  e.stopPropagation();
                  if (!isUpdatingStatus && !isFinalStatus) {
                    setIsEditingStatus(true);
                  }
                }}
                style={{
                  cursor: isUpdatingStatus || isFinalStatus ? 'not-allowed' : 'pointer',
                  opacity: isUpdatingStatus ? 0.6 : 1,
                }}
                title={isFinalStatus ? 'Final status cannot be changed' : undefined}
              />
            ) : (
              <StyledSpanEmptyStatus
                onClick={e => {
                  e.stopPropagation();
                  if (!isUpdatingStatus && !isFinalStatus) {
                    setIsEditingStatus(true);
                  }
                }}
                style={{
                  cursor: isUpdatingStatus || isFinalStatus ? 'not-allowed' : 'pointer',
                  color: '#666',
                  fontStyle: 'italic',
                  opacity: isUpdatingStatus ? 0.6 : 1,
                }}>
                {isUpdatingStatus ? 'Updating...' : 'Click to select status'}
              </StyledSpanEmptyStatus>
            )}
          </>
        )}
      </StyledTableCell>
      <StyledTableCell 
        align="left" 
        onClick={handleNoteClick}
        sx={{ 
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        {renderNotesCell(modelRow)}
      </StyledTableCell>
      <StyledTableCell align="left">
        <Typography variant="body2">
          {modelRow.reviewers?.[0] ? getFullName(modelRow.reviewers?.[0]) : '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell align="left">
        <Typography variant="body2">
          {modelRow.preparers?.[0] ? getFullName(modelRow.preparers?.[0]) : '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell align="left">
        <Typography variant="body2">
          {modelRow.supporting_due_date || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell align="left">
        {renderValuationLeadCell(modelRow)}
      </StyledTableCell>
      <StyledTableCell align="left">
        <Typography variant="body2">
          {(modelRow as ModelWithValuationData).is_loading_valuation_data ? (
            <Skeleton variant="text" width="80%" height={20} />
          ) : (
            (modelRow as ModelWithValuationData).valuation_date || '-'
          )}
        </Typography>
      </StyledTableCell>
      <EditNoteDialog
        open={isNoteDialogOpen}
        onClose={() => setIsNoteDialogOpen(false)}
        onSave={handleNoteSave}
        currentNote={modelRow.note?.content || modelRow.notes || ''}
        companyName={modelRow.company_name || 'Unknown Company'}
        isLoading={isNoteLoading}
        error={noteError}
      />
    </StyledTableRow>
  );
};

export default ModelsTableRow;
